import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

function formatPriceFull(p) {
  if (p === null || p === undefined) return '—';
  return '$' + Number(p).toLocaleString();
}

const bidBadge = {
  pending: 'badge-amber',
  'under-review': 'badge-amber',
  countered: 'badge-purple',
  accepted: 'badge-green',
  declined: 'badge-gray',
};

const bidLabel = {
  pending: 'review',
  'under-review': 'review',
  countered: 'countered',
  accepted: 'accepted',
  declined: 'declined',
};

export default function OwnerDashboard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [bids, setBids] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [zipBuyerCount, setZipBuyerCount] = useState(0);
  const [savedCount, setSavedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const property = properties[0] || null;

  useEffect(() => {
    if (!user) return;
    fetchDashboard();
  }, [user]);

  async function fetchDashboard() {
    setLoading(true);

    const { data: propsData } = await supabase
      .from('properties')
      .select('*')
      .eq('owner_id', user.id)
      .order('listed_at', { ascending: false });

    const activeProperty = (propsData || [])[0] || null;
    setProperties(propsData || []);

    const { data: notifsData } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);
    setNotifications(notifsData || []);

    if (activeProperty) {
      const { data: bidsData } = await supabase
        .from('bids')
        .select(`
          *,
          profiles!buyer_id ( full_name, email )
        `)
        .eq('property_id', activeProperty.id)
        .neq('status', 'withdrawn')
        .order('amount', { ascending: false });
      setBids(bidsData || []);

      const { count: saved } = await supabase
        .from('saved_properties')
        .select('*', { count: 'exact', head: true })
        .eq('property_id', activeProperty.id);
      setSavedCount(saved || 0);

      if (activeProperty.zip) {
        const { data: zipBids } = await supabase
          .from('bids')
          .select('buyer_id, properties!inner(zip)')
          .eq('properties.zip', activeProperty.zip);
        const distinctBuyers = new Set((zipBids || []).map(b => b.buyer_id));
        setZipBuyerCount(distinctBuyers.size);
      } else {
        setZipBuyerCount(0);
      }
    } else {
      setBids([]);
      setSavedCount(0);
      setZipBuyerCount(0);
    }

    setLoading(false);
  }

  async function togglePause() {
    if (!property) return;
    const nextStatus = property.status === 'paused' ? 'listed' : 'paused';
    const { error } = await supabase
      .from('properties')
      .update({ status: nextStatus })
      .eq('id', property.id)
      .eq('owner_id', user.id);
    if (!error) fetchDashboard();
  }

  if (!user) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>Please sign in to view your owner dashboard.</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>go home</button>
    </div>
  );

  if (loading) return (
    <div className="page" style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
      loading your dashboard...
    </div>
  );

  if (!property) return (
    <div className="page" style={{ textAlign: 'center', padding: 60 }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
        welcome{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
      </h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>You don't have a listing yet. List your home to start receiving bids.</p>
      <button className="btn btn-primary" onClick={() => navigate('/owner-list')}>list my home</button>
    </div>
  );

  const activeBids = bids.filter(b => b.status !== 'declined');
  const highestBid = bids.length ? Math.max(...bids.map(b => b.amount || 0)) : null;
  const daysListed = property.listed_at
    ? Math.max(0, Math.floor((Date.now() - new Date(property.listed_at)) / 86400000))
    : 0;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600 }}>
            welcome back{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{property.address} · owner account</p>
        </div>
        <button className="btn" onClick={() => navigate('/faq')}>owner FAQ</button>
      </div>

      <div className="metric-grid">
        {[
          [String(activeBids.length), 'bids received'],
          [formatPriceFull(highestBid), 'highest bid'],
          [String(zipBuyerCount), `buyers in ${property.zip || 'your zip'}`],
          [`${daysListed} day${daysListed === 1 ? '' : 's'}`, 'listed'],
        ].map(([v, l]) => (
          <div className="metric" key={l}><div className="val">{v}</div><div className="lbl">{l}</div></div>
        ))}
      </div>

      <div className="two-col">
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3>your listing</h3>
              <span className={`badge ${property.status === 'paused' ? 'badge-gray' : property.status === 'under-contract' ? 'badge-purple' : 'badge-green'}`}>
                {property.status === 'paused' ? 'paused' : property.status === 'under-contract' ? 'under contract' : 'active'}
              </span>
            </div>
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{property.address}, {property.city} {property.state}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14 }}>
              {property.beds} bd · {property.baths} ba · {property.sqft?.toLocaleString()} sqft · asking {formatPriceFull(property.asking_price)}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <div className="metric" style={{ flex: 1 }}><div className="val" style={{ fontSize: 16 }}>{formatPriceFull(property.zestimate)}</div><div className="lbl">Zestimate</div></div>
              <div className="metric" style={{ flex: 1 }}><div className="val" style={{ fontSize: 16, color: 'var(--green)' }}>{formatPriceFull(highestBid)}</div><div className="lbl">top bid</div></div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/bid/${property.id}`)}>review bids</button>
              <button className="btn btn-sm" onClick={() => navigate('/owner-list')}>edit listing</button>
              <button className="btn btn-sm" onClick={togglePause}>{property.status === 'paused' ? 'resume listing' : 'pause listing'}</button>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ marginBottom: 14 }}>bid activity</h3>
            {bids.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0' }}>
                No bids yet. You'll see buyer activity here as soon as it comes in.
              </div>
            ) : (
              bids.map(b => (
                <div className="row-item" key={b.id}>
                  <div style={{ flex: 1 }}>
                    <div className="row-title">{b.profiles?.full_name || 'Buyer'} — {formatPriceFull(b.amount)}</div>
                    <div className="row-sub">
                      {b.closing_timeline ? `${b.closing_timeline}-day close · ` : ''}
                      {b.status === 'countered' ? 'you sent a counter · awaiting response' : 'pending your review'}
                    </div>
                  </div>
                  <span className={`badge ${bidBadge[b.status] || 'badge-gray'}`}>{bidLabel[b.status] || b.status}</span>
                </div>
              ))
            )}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 12 }}>buyer interest near you</h3>
            {[
              [`buyers bidding in ${property.zip || 'your zip'}`, String(zipBuyerCount)],
              ['saved your address', String(savedCount)],
              ['total bids on this listing', String(bids.length)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '7px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ))}
            <div className="future-gate"><div className="gate-label">detailed buyer analytics — views, saves, time-on-page</div><div className="gate-badge">future premium</div></div>
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 12 }}>quick links</h3>
            {[
              ['next steps after accepting a bid →', '/next-steps'],
              ['owner FAQ →', '/faq'],
              ['find a title company →', '/next-steps'],
              ['find a local agent →', '/next-steps'],
            ].map(([label, path]) => (
              <button key={label} className="btn btn-full" style={{ marginBottom: 8, fontSize: 12, justifyContent: 'flex-start' }} onClick={() => navigate(path)}>{label}</button>
            ))}
          </div>

          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 10 }}>recent notifications</h3>
            {notifications.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '8px 0' }}>
                No notifications yet. You'll hear here when buyers bid or respond.
              </div>
            ) : (
              notifications.map(n => (
                <div className="row-item" key={n.id}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.read ? 'var(--border)' : '#E24B4A', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 500 }}>{n.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.body}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {new Date(n.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="card">
            <h3 style={{ marginBottom: 10 }}>notification settings</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0' }}>
              <span style={{ color: 'var(--text-secondary)' }}>default notification method</span>
              <span style={{ color: 'var(--blue)', fontSize: 12, fontWeight: 500 }}>{profile?.notification_method || 'email'}</span>
            </div>
            <button className="btn btn-full" style={{ marginTop: 12, fontSize: 12 }} onClick={() => navigate('/account')}>edit preferences</button>
          </div>
        </div>
      </div>
    </div>
  );
}
